import { Drawer, Box, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemIcon, ListItemText, Paper, Link } from '@mui/material';
import { ChevronRight, LightbulbOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

interface HelpSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  content: {
    title: string;
    description: string;
    instructions: string[];
    tips: string[];
  };
  width: number;
}

const renderHelpItemText = (text: string) => {
  const colonIndex = text.indexOf(':');
  // If there's a colon early in the sentence (within first 35 chars, before any period), treat as title prefix
  if (colonIndex > 0 && colonIndex <= 35 && !text.slice(0, colonIndex).includes('.')) {
    const title = text.slice(0, colonIndex + 1);
    const rest = text.slice(colonIndex + 1);
    return (
      <Typography variant="body2" sx={{ lineHeight: 1.55, color: 'text.secondary' }}>
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', mr: 0.5 }}>
          {title}
        </Box>
        {rest}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" sx={{ lineHeight: 1.55, color: 'text.secondary' }}>
      {text}
    </Typography>
  );
};

const GenAIDisclaimer = () => {
    const { t } = useTranslation();
    return (
        <Paper elevation={0} sx={{ p: 0 }}>
        <Typography variant="body2" paragraph>
            {t('helpSidebar.disclaimerText1')}
        </Typography>
        <Typography variant="body2" paragraph>
            {t('helpSidebar.disclaimerText2')}
        </Typography>
        <Typography variant="body2" paragraph>
            {t('helpSidebar.learnMore')} <Link href="https://www.tdsynnex.com/us/en/privacy.html" target="_blank" rel="noopener">www.tdsynnex.com/us/en/privacy.html</Link>.
        </Typography>
        <Typography variant="body2" paragraph>
            {t('helpSidebar.supportContact')} <Link href="mailto:synapse-support@tdsynnex.com">synapse-support@tdsynnex.com</Link>.
        </Typography>
        </Paper>
    );
};

export default function HelpSidebar({ isOpen, onClose, content, width }: HelpSidebarProps) {
  const { t } = useTranslation();
  return (
    <Drawer
      sx={{
        width: { xs: '100vw', sm: width },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', sm: width },
          maxWidth: '100vw',
          boxSizing: 'border-box',
        },
      }}
      variant="temporary"
      anchor="right"
      open={isOpen}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 700 }}>{t('helpSidebar.title')}</Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close help sidebar">
          <ChevronRight sx={{ color: 'text.primary' }} />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: '1.25rem', mb: 2 }}>
          {content.title}
        </Typography>

        <Accordion defaultExpanded disableGutters sx={{ mb: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{t('helpSidebar.pageDescription')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{content.description}</Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ mb: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{t('helpSidebar.instructions')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 1.5 }}>
            <List dense disablePadding>
              {content.instructions.map((step, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  alignItems="flex-start"
                  sx={{
                    py: 1,
                    px: 0,
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 26,
                      mr: 1.25,
                      mt: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={renderHelpItemText(step)}
                    sx={{ my: 0 }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ mb: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{t('helpSidebar.tips')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 1.5 }}>
            <List dense disablePadding>
              {content.tips.map((tip, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  alignItems="flex-start"
                  sx={{
                    py: 1,
                    px: 0,
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 26,
                      mr: 1.25,
                      mt: '2px',
                    }}
                  >
                    <LightbulbOutlined
                      sx={{
                        fontSize: 20,
                        color: 'warning.main',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={renderHelpItemText(tip)}
                    sx={{ my: 0 }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ mt: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{t('helpSidebar.disclaimerTitle')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
            <GenAIDisclaimer />
          </AccordionDetails>
        </Accordion>

      </Box>
    </Drawer>
  );
}
